import React from 'react'

interface LogoLogixPortProps {
  /**
   * 'icon': Solo el isotipo del cubo portal 3D
   * 'full': Isotipo + Texto "LogixPort" + Subtítulo / Badge
   */
  variant?: 'icon' | 'full'
  /**
   * Tamaño del icono
   */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /**
   * Muestra la insignia v1.0
   */
  conBadge?: boolean
  /**
   * Muestra el subtítulo "PORT & LOGISTICS OS"
   */
  conSubtitulo?: boolean
  className?: string
}

/**
 * Isotipo Minimalista Oficial de LogixPort (Concepto Portal Isométrico 3D).
 * Inspirado en la pureza geométrica de Vercel y Apple:
 * Cubo isométrico con portal abierto en el muelle derecho que representa
 * el WMS tridimensional, el contenedor de carga y la entrada al puerto aduanero.
 */
export const IsotipoLogixPort: React.FC<{ sizeCls?: string; className?: string }> = ({
  sizeCls = 'h-8 w-8',
  className = ''
}) => {
  return (
    <div className={`relative flex items-center justify-center shrink-0 ${sizeCls} ${className}`}>
      <svg
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full drop-shadow-sm transition-transform duration-300 group-hover:scale-105"
      >
        <defs>
          {/* Cara Superior (Techo Isométrico / Almacén) */}
          <linearGradient id="lpTopGrad" x1="18" y1="16" x2="82" y2="48" gradientUnits="userSpaceOnUse">
            <stop offset="0%" className="[stop-color:#ffffff] dark:[stop-color:#f8fafc]" />
            <stop offset="100%" className="[stop-color:#e2e8f0] dark:[stop-color:#e2e8f0]" />
          </linearGradient>

          {/* Cara Izquierda (Muro de Carga / Estructura) */}
          <linearGradient id="lpLeftGrad" x1="18" y1="32" x2="50" y2="84" gradientUnits="userSpaceOnUse">
            <stop offset="0%" className="[stop-color:#cbd5e1] dark:[stop-color:#94a3b8]" />
            <stop offset="100%" className="[stop-color:#94a3b8] dark:[stop-color:#64748b]" />
          </linearGradient>

          {/* Cara Derecha (Frontal con Portal Abierto) */}
          <linearGradient id="lpRightGrad" x1="50" y1="32" x2="82" y2="84" gradientUnits="userSpaceOnUse">
            <stop offset="0%" className="[stop-color:#f1f5f9] dark:[stop-color:#cbd5e1]" />
            <stop offset="100%" className="[stop-color:#e2e8f0] dark:[stop-color:#94a3b8]" />
          </linearGradient>

          {/* Interior del Portal (Profundidad Digital / Núcleo Cian) */}
          <linearGradient id="lpPortalGrad" x1="61" y1="46" x2="74" y2="78" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#06b6d4" />
            <stop offset="100%" stopColor="#2563eb" />
          </linearGradient>
        </defs>

        {/* 1. Cara Superior */}
        <path
          d="M 50 16 L 82 32 L 50 48 L 18 32 Z"
          fill="url(#lpTopGrad)"
          className="text-slate-900 dark:text-white"
        />

        {/* 2. Cara Izquierda */}
        <path
          d="M 18 32 L 50 48 L 50 84 L 18 68 Z"
          fill="url(#lpLeftGrad)"
        />

        {/* 3. Interior del Portal (Profundidad Tecnológica) */}
        <path
          d="M 61.2 50.4 L 74 44 L 74 72 L 61.2 78.4 Z"
          fill="url(#lpPortalGrad)"
        />

        {/* 4. Cara Derecha con Entrada de Portal */}
        <path
          d="M 50 48 L 82 32 L 82 68 L 74 72 L 74 44 L 61.2 50.4 L 61.2 78.4 L 50 84 Z"
          fill="url(#lpRightGrad)"
        />

        {/* Aristas Isométricas Sutiles de Definición */}
        <path
          d="M 50 16 L 50 48 M 18 32 L 50 48 M 82 32 L 50 48"
          stroke="rgba(0,0,0,0.12)"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </svg>

      {/* Punto de Telemetría Activa (Live Pulse) */}
      <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2 pointer-events-none">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
      </span>
    </div>
  )
}

export const LogoLogixPort: React.FC<LogoLogixPortProps> = ({
  variant = 'full',
  size = 'md',
  conBadge = true,
  conSubtitulo = true,
  className = ''
}) => {
  const sizeMap = {
    sm: { box: 'h-7 w-7', text: 'text-sm', badge: 'text-[8px]', sub: 'text-[8px]' },
    md: { box: 'h-9 w-9', text: 'text-base', badge: 'text-[9px]', sub: 'text-[9px]' },
    lg: { box: 'h-11 w-11', text: 'text-xl', badge: 'text-[10px]', sub: 'text-[10px]' },
    xl: { box: 'h-14 w-14', text: 'text-2xl', badge: 'text-xs', sub: 'text-xs' }
  }

  const s = sizeMap[size]

  if (variant === 'icon') {
    return (
      <div className={`flex items-center ${className}`}>
        <div className={`flex items-center justify-center rounded-2xl bg-gradient-to-tr from-slate-900 via-slate-800 to-slate-950 p-1.5 shadow-md shadow-slate-950/20 dark:from-white/10 dark:via-white/5 dark:to-transparent dark:border dark:border-white/10 ${s.box}`}>
          <IsotipoLogixPort sizeCls="w-full h-full" />
        </div>
      </div>
    )
  }

  return (
    <div className={`flex items-center gap-2.5 group ${className}`}>
      {/* Contenedor del Isotipo con fondo Bento Glass */}
      <div className={`relative flex items-center justify-center rounded-2xl bg-gradient-to-tr from-slate-950 via-slate-900 to-blue-950 p-1.5 shadow-md shadow-cyan-500/10 border border-slate-800/80 group-hover:scale-105 transition-all duration-300 dark:from-white/10 dark:via-white/5 dark:to-cyan-500/10 dark:border-white/15 ${s.box}`}>
        <IsotipoLogixPort sizeCls="w-full h-full" />
      </div>

      {/* Wordmark Tipográfico */}
      <div className="leading-tight">
        <div className="flex items-center gap-1.5">
          <p className={`font-black tracking-tight text-slate-950 dark:text-white ${s.text}`}>
            Logix<span className="text-cyan-500">Port</span>
          </p>
          {conBadge && (
            <span className={`font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20 ${s.badge}`}>
              v1.0
            </span>
          )}
        </div>
        {conSubtitulo && (
          <p className={`font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-zinc-500 ${s.sub}`}>
            PORT & LOGISTICS OS
          </p>
        )}
      </div>
    </div>
  )
}

export default LogoLogixPort
